<template lang="pug"  src="./cashback-editor.pug"> </template>
<style lang="scss" src="./cashback-editor.scss" scoped> </style>

<script>
import validate from '@/mixins/validate'
import request from '@/utils/request'

import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import scroll from '@/utils/scroll'

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        system: {
            sequences: []
        },
        groups: [],
        sequence: {}
    }),
    validations: {
        system: {
            name: {
                required
            },
            groupId: {
                required
            },
            fromDate: {
                required
            },
            toDate: {
                required
            }
        }
    },
    computed: {
        isDeliveryGroup() {
            if (!this.system.groupId) {
                return false
            }

            const groupInfo = this.groups.find(g => g.id === this.system.groupId)

            return groupInfo ? groupInfo.isDelivery : false
        },
        fromDateMin() {
            return new Date().addDays(-1).toJSON()
        },
        fromDateMax() {
            return new Date().addDays(20).toJSON()
        },
        toDateMin() {
            const fromDate = this.system.fromDate && new Date(this.system.fromDate)
            const curDate = new Date()

            if (curDate > fromDate) {
                return curDate.toJSON()
            }

            return fromDate && fromDate.toJSON()
        },
        toDateMax() {
            return new Date().addDays(365).toJSON()
        },
        completedGroups() {
            if (this.system.group) {
                return this.groups.concat([{ ...this.system.group }])
            }

            return this.groups
        },
        nextSequencesOrder() {
            if (this.sequence.id) {
                return this.sequence.order
            }

            return this.system.sequences && this.system.sequences.length || 0
        },
        disabledChangeSequence() {
            const { percent, text } = this.sequence

            if (percent) {
                return percent > 100 || percent <= 0
            }

            if (text) {
                return text.length > 255
            }

            return false
        },
        disabledChangeActive() {
            return this.system.sequences && !this.system.sequences.length
        },
        endpointRoot() {
            switch (this.bonusSystemType) {
                case 'cashbackPercent':
                    return '/cashback'

                case 'sum':
                    return '/reach-amount-bonus'

                case 'count':
                    return '/time-limited-bonus'
            }

            return '/'
        },
        bonusSystemType() {
            return this.$route.query.type
        }
    },
    created: function() {
        if (!this.bonusSystemType || !['cashbackPercent', 'sum', 'count'].includes(this.bonusSystemType)) {
            return this.$router.push('/cashback')
        }

        this.getGroups()

        if (this.$route.params.id) {
            this.getSystemData(this.$route.params.id)
        }
    },
    methods: {
        changeEditorDataValue(field, value) {
            this.$set(this.system, field, value)
        },

        generateHeaders(bonusSystem) {
            const headers = []

            switch (bonusSystem) {
                case 'cashbackPercent':
                    headers.push('order', 'percent', 'text')
                    break

                case 'sum':
                    headers.push('sum', 'amount', 'text')
                    break

                case 'count':
                    headers.push('count', 'amount', 'text')
            }

            if (this.canEdit()) {
                headers.push('actions')
            }

            return headers.map(text => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.CashbackEditor.table.${text}`),
                sortable: false
            }))
        },

        async getGroups() {
            await request({
                method: 'get',
                url: this.endpointRoot + '/groups'
            }).then(res => {
                if (res.data) {
                    this.groups = res.data.groups
                }
            })
        },

        async getSystemData(id) {
            await request({
                method: 'get',
                url: this.endpointRoot + `/${id}`
            }).then(res => {
                if (res.data) {
                    this.updateSystemData(res.data)
                }
            }).catch(() => {
                this.$router.push(`/cashback/editor?type=` + this.bonusSystemType)
            })
        },

        async changeSystemStatus() {
            await request({
                method: 'patch',
                url: this.endpointRoot + `/${this.system.id}/change-active`
            }).then(res => {
                if (res.data) {
                    this.updateSystemData(res.data)
                }
            }).catch(this.errorHandler)
        },

        async completeSystem() {
            await request({
                method: 'delete',
                url: this.endpointRoot + `/${this.system.id}`
            }).then(res => {
                if (res.status == 204) {
                    this.$router.push(`/cashback`)
                }
            }).catch(this.errorHandler)
        },

        async createOrSaveSystem() {
            if (this.$v.system.$invalid) {
                return this.$v.system.$touch()
            }

            const method = this.system.id ? 'put' : 'post'
            const url = this.system.id ? this.endpointRoot + `/${this.system.id}` : this.endpointRoot

            await request({ method, url, data: this.system })
                .then(res => {
                    if (res.status == 201) {
                        this.$router.push(`/cashback/editor/${res.data.id}?type=` + this.bonusSystemType)
                    }

                    this.updateSystemData(res.data)

                    this.$store.dispatch('addMessage', {
                        type: 'success',
                        message: 'success',
                        locale: true
                    }, { root: true })
                }).catch(this.errorHandler)
        },

        updateSystemData(data) {
            const {
                id, enabled, wasStartedUp, groupId, fromDate, toDate,
                group, name, steps: sequences, includeTelephony
            } = data

            this.system = Object.assign({}, this.system, {
                id, enabled, wasStartedUp, name, includeTelephony,
                group: this.system.group || group,
                sequences: sequences && sequences.length ? sequences.map(s => {
                    switch (this.bonusSystemType) {
                        case 'cashbackPercent':
                            return s

                        case 'sum':
                            s.sum = s.bonusCondition
                            s.amount = s.bonusReward
                            s.order = s.id
                            delete s.bonusCondition
                            delete s.bonusReward

                            return s

                        case 'count':
                            s.count = s.bonusCondition
                            s.amount = s.bonusReward
                            s.order = s.id
                            delete s.bonusCondition
                            delete s.bonusReward

                            return s
                    }
                }).sortBy('order') : this.system.sequences,
                groupId: `${groupId}`,
                fromDate: new Date(fromDate).format('{yyyy}-{MM}-{dd}'),
                toDate: new Date(toDate).format('{yyyy}-{MM}-{dd}')
            })
        },

        canEdit(field) {
            if (!this.system.id || (!this.system.wasStartedUp && !this.system.enabled)) {
                return true
            }

            const fromDate = new Date(this.system.fromDate).format('{yyyy}-{MM}-{dd}')
            const toDate = new Date(this.system.toDate).format('{yyyy}-{MM}-{dd}')
            const curDate = new Date().format('{yyyy}-{MM}-{dd}')

            return !(this.system.enabled || toDate <= curDate || (fromDate <= curDate && field != 'toDate'))
        },

        checkEditedRow(id) {
            return id == this.sequence.id
        },

        editSequence(item) {
            this.sequence = { ...item }

            if (this.$refs.sequence) {
                scroll(this.$refs.sequence)
            }
        },

        cancelEdit() {
            this.sequence = {}
        },

        async removeSequence(id) {
            await request({
                method: 'delete',
                url: this.endpointRoot + `/${this.system.id}/${id}`
            }).then(res => {
                if (res.status == 204) {
                    this.system.sequences.pop()
                    this.sequence = {}
                    this.$store.dispatch('addMessage', {
                        type: 'success',
                        message: 'success',
                        locale: true
                    }, { root: true })
                }
            }).catch(this.errorHandler)
        },

        async saveSequence() {
            const method = this.sequence.id ? 'put' : 'post'
            const path = this.endpointRoot + `/${this.system.id}/`
            const pathEnd = this.sequence.id ? `${this.sequence.id}` : `step`
            const conditionAndReward = {}

            switch (this.bonusSystemType) {
                case 'cashbackPercent':
                    conditionAndReward.bonusCondition = parseInt(this.sequence.percent) || null
                    break

                case 'sum':
                    conditionAndReward.bonusCondition = parseInt(this.sequence.sum) || null
                    conditionAndReward.bonusReward = parseInt(this.sequence.amount) || null
                    break

                case 'count':
                    conditionAndReward.bonusCondition = parseInt(this.sequence.count) || null
                    conditionAndReward.bonusReward = parseInt(this.sequence.amount) || null
                    break
            }

            await request({
                method,
                url: path + pathEnd,
                data: {
                    text: this.sequence.text || '',
                    ...conditionAndReward
                }
            }).then(res => {
                switch (this.bonusSystemType) {
                    case 'cashbackPercent':
                        break

                    case 'sum':
                        res.data.sum = res.data.bonusCondition
                        res.data.amount = res.data.bonusReward
                        res.data.order = res.data.id
                        delete res.data.bonusCondition
                        delete res.data.bonusReward
                        break

                    case 'count':
                        res.data.count = res.data.bonusCondition
                        res.data.amount = res.data.bonusReward
                        res.data.order = res.data.id
                        delete res.data.bonusCondition
                        delete res.data.bonusReward
                }

                if (!this.sequence.id) {
                    this.system.sequences.push(res.data)
                } else {
                    const index = this.system.sequences.findIndex(it => it.id === this.sequence.id)

                    this.$set(this.system.sequences, index, res.data)
                }

                this.sequence = {}

                this.$store.dispatch('addMessage', {
                    type: 'success',
                    message: 'success',
                    locale: true
                }, { root: true })
            }).catch(this.errorHandler)
        },

        errorHandler(error) {
            if (error.response && error.response.data && error.response.data.message) {
                this.$store.dispatch('addMessage', {
                    type: 'error',
                    message: error.response.data.message,
                    locale: false
                }, { root: true })
            }
        },

        getSequenceIcon(id) {
            return id ? 'done_outline' : 'add'
        }
    }
}
</script>
